import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { countyAPI, projectAPI, wardAPI, budgetAPI } from '../services/api';
import { County } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';

interface HomeStats {
  totalBudget: number;
  population: number;
  wardCount: number;
  projectCount: number;
  sectorBreakdown: { [key: string]: number };
}

const HomePage: React.FC = () => {
  const [county, setCounty] = useState<County | null>(null);
  const [stats, setStats] = useState<HomeStats>({
    totalBudget: 0,
    population: 0,
    wardCount: 0,
    projectCount: 0,
    sectorBreakdown: {}
  });
  const [currentFiscalYear, setCurrentFiscalYear] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadCountyData();
  }, []);

  const loadCountyData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch county data
      const countyResponse = await countyAPI.getById('Turkana');
      setCounty(countyResponse.data);
      
      // Fetch budgets (uploaded Excel data)
      const budgetsResponse = await budgetAPI.getAll();
      const budgets = budgetsResponse.data;
      
      // Fetch analytics data
      const analyticsResponse = await countyAPI.getAnalytics('Turkana');
      const analytics = analyticsResponse.data;
      
      // Fetch wards
      const wardsResponse = await wardAPI.getByCounty('Turkana');
      const wards = wardsResponse.data;
      
      // Fetch projects
      const projectsResponse = await projectAPI.getPublic('Turkana');
      const projects = projectsResponse.data;
      
      // Use uploaded budget if available, otherwise use county budget or analytics
      let totalBudget = 0;
      if (budgets && budgets.length > 0) {
        // Use the most recent uploaded budget
        totalBudget = budgets[0].total_budget;
      } else if (countyResponse.data.total_budget) {
        totalBudget = countyResponse.data.total_budget;
      } else {
        totalBudget = analytics.total_budget || 0;
      }

      // Get the most recent fiscal year from budgets
      if (budgets && budgets.length > 0 && budgets[0].fiscal_year) {
        setCurrentFiscalYear(budgets[0].fiscal_year);
      } else {
        // Fall back to fiscal-years endpoint
        try {
          const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
          const fyRes = await fetch(`${API_BASE}/projects/fiscal-years`);
          if (fyRes.ok) {
            const years = await fyRes.json();
            if (years.length > 0) setCurrentFiscalYear(years[0]);
          }
        } catch {}
      }
      
      // Calculate sector breakdown percentages
      const sectorBudgets = analytics.budget_by_sector || {};
      const sectorBreakdown: { [key: string]: number } = {};
      
      Object.keys(sectorBudgets).forEach(sector => {
        sectorBreakdown[sector] = totalBudget > 0 ? (sectorBudgets[sector] / totalBudget) * 100 : 0;
      });
      
      setStats({
        totalBudget: totalBudget,
        population: countyResponse.data.population || 0,
        wardCount: wards.length || 30,
        projectCount: projects.length || 0,
        sectorBreakdown
      });
      
    } catch (error: any) {
      console.error('Failed to load county data:', error);
      
      // Set error message based on error type
      if (error.message.includes('timeout')) {
        setError('Connection timeout. The server may be starting up.');
      } else if (error.message.includes('503')) {
        setError('Service temporarily unavailable. The server is starting up.');
      } else {
        setError('Unable to connect to server.');
      }
      
      setCounty(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingSpinner 
        message="Loading Turkana County data..." 
        showRetryMessage={true}
        onRetry={loadCountyData}
      />
    );
  }

  return (
    <div className="home-page">
      {error && (
        <div className="error-banner">
          <p>⚠️ {error}</p>
          <p><button onClick={loadCountyData} className="retry-link">Retry</button></p>
        </div>
      )}
      
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
           
            <span>Compatriots Platform</span>
          </div>
          <h1>Turkana Budgets and Accountability Platform</h1>
          <p>Empowering citizens through open governance, transparent budgeting, and accountable project implementation across all 30 wards of Turkana County.</p>
          
          <div className="hero-actions">
            <Link to="/dashboard" className="cta-primary">
              <span>📊</span>
              Explore Dashboard
            </Link>
            <Link to="/dashboard" className="cta-secondary">
              <span>🏗️</span>
              View Projects
            </Link>
          </div>

          <div className="hero-stats">
            <div className="stat">
              <div className="stat-icon">💰</div>
              <div className="stat-content">
                <h3>KES {stats.totalBudget > 0 ? (stats.totalBudget / 1e9).toFixed(1) : '0'}B</h3>
                <p>Total Budget{currentFiscalYear ? ` FY ${currentFiscalYear}` : ''}</p>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <h3>{stats.population > 0 ? (stats.population / 1e6).toFixed(1) : '0'}M</h3>
                <p>Population Served</p>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">📍</div>
              <div className="stat-content">
                <h3>{stats.wardCount}</h3>
                <p>Administrative Wards</p>
              </div>
            </div>
            <div className="stat">
              <div className="stat-icon">🏗️</div>
              <div className="stat-content">
                <h3>{stats.projectCount}</h3>
                <p>{stats.projectCount === 0 ? 'Projects' : stats.projectCount === 1 ? 'Active Project' : 'Active Projects'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="features">
        <div className="section-header">
          <h2>Platform Capabilities</h2>
          <p>Comprehensive tools for budget transparency and citizen engagement</p>
        </div>
        
        <div className="features-grid">
          <Link to="/dashboard" className="feature-card interactive">
            <div className="feature-icon">📊</div>
            <div className="feature-content">
              <h3>Budget Dashboard</h3>
              <p>Interactive visualizations of county budget allocation across sectors and wards with real-time analytics</p>
              <span className="feature-arrow">→</span>
            </div>
          </Link>
          
          <Link to="/dashboard" className="feature-card interactive">
            <div className="feature-icon">🏗️</div>
            <div className="feature-content">
              <h3>Project Tracking</h3>
              <p>Monitor development projects from inception to completion with progress updates and timelines</p>
              <span className="feature-arrow">→</span>
            </div>
          </Link>
          
          <Link to="/documents" className="feature-card interactive">
            <div className="feature-icon">📚</div>
            <div className="feature-content">
              <h3>Document Repository</h3>
              <p>Access official county documents, budgets, plans, and reports organized by sector and ward</p>
              <span className="feature-arrow">→</span>
            </div>
          </Link>
          
          <Link to="/forum" className="feature-card interactive">
            <div className="feature-icon">💬</div>
            <div className="feature-content">
              <h3>Community Forum</h3>
              <p>Engage in public discussions and share feedback with fellow citizens</p>
              <span className="feature-arrow">→</span>
            </div>
          </Link>
          
          <Link to="/feedback" className="feature-card interactive">
            <div className="feature-icon">📝</div>
            <div className="feature-content">
              <h3>Citizen Feedback</h3>
              <p>Submit feedback, report issues, and track responses from county officials</p>
              <span className="feature-arrow">→</span>
            </div>
          </Link>
          

        </div>
      </section>

      <section className="transparency">
        <div className="transparency-content">
          <div className="transparency-text">
            <h2>Our Commitment to Transparency</h2>
            <p>This platform embodies our dedication to open governance and citizen participation in Turkana County's development journey.</p>
            
            <div className="commitment-list">
              <div className="commitment-item">
                <span className="commitment-icon">✅</span>
                <div>
                  <strong>Open Budget Data</strong>
                  <p>Complete budget breakdown accessible to all citizens</p>
                </div>
              </div>
              
              <div className="commitment-item">
                <span className="commitment-icon">✅</span>
                <div>
                  <strong>Real-time Project Updates</strong>
                  <p>Live progress tracking and status updates</p>
                </div>
              </div>
              
              <div className="commitment-item">
                <span className="commitment-icon">✅</span>
                <div>
                  <strong>Public Participation</strong>
                  <p>Citizen feedback and community engagement</p>
                </div>
              </div>
              
              <div className="commitment-item">
                <span className="commitment-icon">✅</span>
                <div>
                  <strong>Accountability Measures</strong>
                  <p>Clear reporting and performance tracking</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="transparency-visual">
            <div className="transparency-card">
              <h3>📈 Impact Metrics</h3>
              <div className="metrics">
                <div className="metric">
                  <span className="metric-value">{stats.totalBudget > 0 ? '100%' : '0%'}</span>
                  <span className="metric-label">Budget Transparency</span>
                </div>
                <div className="metric">
                  <span className="metric-value">{stats.projectCount}</span>
                  <span className="metric-label">Active Projects</span>
                </div>
                <div className="metric">
                  <span className="metric-value">{stats.wardCount}</span>
                  <span className="metric-label">Wards Covered</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-content">
          <h2>Get Started Today</h2>
          <p>Join thousands of Turkana County citizens in promoting transparency and accountability</p>
          
          <div className="cta-buttons">
            <Link to="/dashboard" className="cta-btn primary">
              <span>📊</span>
              Explore Data
            </Link>
            <Link to="/feedback" className="cta-btn secondary">
              <span>💬</span>
              Share Feedback
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
  

};

export default HomePage;